import { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Carousel } from "primereact/carousel";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import CategoryCard from "./CategoryCard";
import { type CategoryScore, type TopicScore } from "@/constants/assessmentsReport.constants";

export type { CategoryScore };

interface CategoriesCarouselProps {
  categoryScores: CategoryScore[];
  topicScores: Record<string, TopicScore[]>;
  isLoading: boolean;
  hasFilteredData?: boolean;
}

const SKELETON_ITEMS = [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }];

const responsiveOptions = [
  { breakpoint: "1600px", numVisible: 3, numScroll: 1 },
  { breakpoint: "1200px", numVisible: 2, numScroll: 1 },
  { breakpoint: "1000px", numVisible: 1, numScroll: 1 },
];

export default function CategoriesCarousel({
  categoryScores,
  topicScores,
  isLoading,
  hasFilteredData = true,
}: CategoriesCarouselProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [carouselKey, setCarouselKey] = useState(0);

  useEffect(() => {
    const handleResize = () => setCarouselKey(prev => prev + 1);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const skeletonTemplate = () => (
    <div className="max-w-2xl mr-4 h-full">
      <Card className={`h-full ${themeClasses.bgElevation3}`}>
        <Skeleton width="70%" height="1.5rem" className="mb-2" />
        <Skeleton width="50%" className="mb-3" />
        <Skeleton height="1.5rem" />
      </Card>
    </div>
  );

  const categoryTemplate = ({ category, averageScore }: CategoryScore) => (
    <div className="max-w-2xl mr-4 h-full">
      <CategoryCard
        key={category}
        category={category}
        averageScore={averageScore}
        topicScores={topicScores[category]}
        isCollapsed={isCollapsed}
      />
    </div>
  );

  const hasData = categoryScores && categoryScores.length > 0;
  const shouldShowEmptyState = !isLoading && !hasData;

  return (
    <div className="mb-12">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-base font-semibold">Assessment categories</h3>
        <Button
          icon={isCollapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
          unstyled
          className={C("cursor-pointer", "text-color")}
          onClick={() => setIsCollapsed(!isCollapsed)}
        />
      </div>

      {shouldShowEmptyState ? (
        <Card className={C(themeClasses.bgElevation2, "max-w-xl")}>
          <p className="body-sm">
            {hasFilteredData ? "No data available for the selected filters" : "No data available"}
          </p>
        </Card>
      ) : (
        <Carousel
          key={carouselKey}
          value={isLoading ? SKELETON_ITEMS : categoryScores}
          numVisible={5}
          numScroll={1}
          showNavigators={false}
          responsiveOptions={responsiveOptions}
          itemTemplate={isLoading ? skeletonTemplate : categoryTemplate}
          className="[&_.p-carousel-items-container]:items-stretch [&_.p-carousel-item]:flex [&_.p-carousel-item]:items-stretch [&_.p-carousel-item>*]:w-full"
        />
      )}
    </div>
  );
}
